import { useEffect, useRef, useState } from "react";
import { Send, ShieldAlert, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const REASON = {
  personal: ["Zoya Ma'am blocked this: never share phone numbers or private details.", "ज़ोया मैम ने रोका: फ़ोन नंबर या निजी जानकारी कभी शेयर न करें।"],
  unsafe: ["Zoya Ma'am blocked this: please keep the chat kind and safe.", "ज़ोया मैम ने रोका: बातचीत अच्छी और सुरक्षित रखें।"],
};

const Line = ({ e, mine, i }) => {
  const { L } = useLang();
  if (e.kind === "sys") return <li className="text-center text-[11px] text-slate-500" data-testid={`room-event-${i}`}>{e.name} {e.text === "joined" ? L("joined", "जुड़े") : L("left", "चले गए")}</li>;
  if (e.kind === "blocked") return <li className="text-xs rounded-xl bg-[#FFE4EC] text-[#A3123E] px-3 py-2" data-testid={`room-event-${i}`}><b>{e.name}</b> · {L("blocked", "रोका गया")} ({e.reason}): {e.text}</li>;
  if (e.role === "zoya") {
    return (
      <li className={`rounded-2xl px-3 py-2.5 text-sm ${e.summary ? "bg-[#0A192F] text-white" : "bg-[#FFF8E6] border border-[#FFC94D] text-[#0A192F]"}`} data-testid={e.summary ? "room-summary-msg" : `room-event-${i}`}>
        <div className="text-[11px] font-bold inline-flex items-center gap-1 mb-0.5"><Sparkles className="w-3 h-3" />Zoya Ma'am{e.summary ? L(" · group summary", " · ग्रुप सारांश") : ""}</div>
        <div className="whitespace-pre-wrap">{e.summary ? L(e.text, e.text_hi || e.text) : e.text}</div>
      </li>
    );
  }
  return (
    <li className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`room-event-${i}`}>
      <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-[#FF3B3B] text-white rounded-br-md" : e.role === "teacher" ? "bg-[#E0FAFF] border border-[#00B8D4]/50" : "bg-white border border-slate-200"}`}>
        {!mine && <div className="text-[11px] font-bold opacity-70">{e.name}{e.role === "teacher" ? L(" · trainer", " · ट्रेनर") : ""}</div>}
        <div className="whitespace-pre-wrap break-words">{e.text}</div>
      </div>
    </li>
  );
};

export const RoomChat = ({ events, me, myRole = "student", closed, onSend, teacherView = false }) => {
  const { L } = useLang();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(null);
  const box = useRef(null);
  const lines = events.filter((e) => e.kind === "msg" || e.kind === "sys" || (teacherView && e.kind === "blocked"));
  useEffect(() => { box.current?.scrollTo({ top: 1e6, behavior: "smooth" }); }, [lines.length]);
  const submit = async (ev) => {
    ev.preventDefault();
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    try { const r = await onSend(t); setBlocked(r?.blocked || null); if (!r?.blocked) setText(""); }
    catch { setBlocked("error"); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-3xl bg-[#E8F0FE] border border-slate-200 p-3 flex flex-col min-h-[360px]" data-testid="room-chat">
      <ul ref={box} className="flex-1 overflow-y-auto space-y-2 max-h-[420px] pr-1" data-testid="room-messages">
        {lines.length === 0 && <li className="text-sm text-slate-500 text-center py-6">{L("Say hello and share your first idea!", "नमस्ते कहिए और अपना पहला आइडिया बताइए!")}</li>}
        {lines.map((e) => <Line key={e.seq} e={e} i={e.seq} mine={e.name === me && e.role === myRole} />)}
      </ul>
      {blocked && <p className="mt-2 text-xs font-semibold text-[#A3123E] inline-flex items-start gap-1" data-testid="room-blocked-note"><ShieldAlert className="w-4 h-4 shrink-0" />
        {blocked === "error" ? L("Couldn't send. Try again.", "भेजा नहीं जा सका। फिर कोशिश करें।") : L(...(REASON[blocked] || REASON.unsafe))}</p>}
      {closed ? <p className="mt-2 text-xs text-slate-500 text-center" data-testid="room-closed-note">{L("This room is closed.", "यह रूम बंद हो गया है।")}</p> : (
        <form onSubmit={submit} className="mt-2 flex items-center gap-2">
          <input value={text} onChange={(e) => setText(e.target.value)} maxLength={300} data-testid="room-input" placeholder={L("Type to your group…", "ग्रुप को लिखिए…")}
            className="flex-1 min-w-0 h-11 px-4 rounded-full bg-white border border-slate-300 focus:border-[#00B8D4] outline-none text-sm" />
          <button type="submit" disabled={busy || !text.trim()} onMouseDown={(e) => e.preventDefault()} aria-label="Send" data-testid="room-send-btn"
            className="w-11 h-11 shrink-0 rounded-full grid place-items-center bg-[#FF3B3B] text-white disabled:opacity-40"><Send className="w-5 h-5" /></button>
        </form>
      )}
    </div>
  );
};
